import API from "../context/API";
import performanceMonitor, { monitorCacheOperation } from "../utils/performanceMonitor";

// Cache management
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
const cache = new Map();

export const getCacheKey = (endpoint, params) => {
  return `${endpoint}_${JSON.stringify(params)}`;
};

export const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    monitorCacheOperation(true);
    return cached.data;
  }
  monitorCacheOperation(false);
  return null;
};

export const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Fetch categories with pagination and search - OPTIMIZED
export const fetchCategories = async ({ page, query, limit = 50 }) => {
  const cacheKey = getCacheKey('categories', { page, query, limit });
  const cached = getCachedData(cacheKey);
  
  if (cached) {
    return cached;
  }

  // Only add timestamp for search queries or page changes
  const timestamp = query || page > 1 ? Date.now() : undefined;
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    search: query || ''
  });
  
  if (timestamp) {
    params.append('_t', timestamp.toString());
  }
  
  const response = await API.get(`/categories/paginate?${params}`);
  
  // Cache the response
  setCachedData(cacheKey, response.data);
  return response.data;
};

// Fetch items for a specific category - OPTIMIZED
export const fetchCategoryItems = async (categoryId) => {
  const cacheKey = getCacheKey('categoryItems', { categoryId });
  const cached = getCachedData(cacheKey);
  
  if (cached) {
    return cached;
  }

  performanceMonitor.startTimer('fetchCategoryItems');
  
  try {
    const response = await API.get(`/categories/${categoryId}/items`);
    performanceMonitor.endTimer('fetchCategoryItems');
    
    const data = response.data.data || response.data.items || response.data || [];
    
    // Cache the response
    setCachedData(cacheKey, data);
    return data;
  } catch (error) {
    performanceMonitor.endTimer('fetchCategoryItems');
    throw error;
  }
};

// Batch fetch multiple categories with items - NEW OPTIMIZED METHOD
export const fetchCategoriesWithItems = async ({ page, query, limit = 50 }) => {
  const cacheKey = getCacheKey('categoriesWithItems', { page, query, limit });
  const cached = getCachedData(cacheKey);
  
  if (cached) {
    return cached;
  }

  performanceMonitor.startTimer('fetchCategoriesWithItems');
  
  try {
    // Fetch categories first
    const categoriesResponse = await fetchCategories({ page, query, limit });
    
    if (!categoriesResponse.data || categoriesResponse.data.length === 0) {
      performanceMonitor.endTimer('fetchCategoriesWithItems');
      return categoriesResponse;
    }

    // Fetch items for all categories in parallel
    const categoryIds = categoriesResponse.data.map(cat => cat.id);
    const itemsPromises = categoryIds.map(catId => 
      fetchCategoryItems(catId).catch(err => {
        console.warn(`Failed to fetch items for category ${catId}:`, err);
        return [];
      })
    );

    const itemsResults = await Promise.all(itemsPromises);
    
    // Combine categories with their items
    const enrichedCategories = categoriesResponse.data.map((category, index) => ({
      ...category,
      items: itemsResults[index] || []
    }));

    const result = {
      ...categoriesResponse,
      data: enrichedCategories
    };

    // Cache the combined result
    setCachedData(cacheKey, result);
    performanceMonitor.endTimer('fetchCategoriesWithItems');
    return result;
    
  } catch (error) {
    performanceMonitor.endTimer('fetchCategoriesWithItems');
    console.error('Error in batch fetch:', error);
    throw error;
  }
};

// Fetch items with larger page size - OPTIMIZED
export const fetchItems = async ({ page, query, limit = 100 }) => {
  const cacheKey = getCacheKey('items', { page, query, limit });
  const cached = getCachedData(cacheKey);
  
  if (cached) {
    return cached;
  }

  performanceMonitor.startTimer('fetchItems');
  
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search: query || ''
    });

    const response = await API.get(`/items/paginate?${params}`);
    performanceMonitor.endTimer('fetchItems');
    
    // Cache the response
    setCachedData(cacheKey, response.data);
    return response.data;
  } catch (error) {
    performanceMonitor.endTimer('fetchItems');
    throw error;
  }
};

// Clear cache for specific data types
export const clearCache = (type = 'all') => {
  if (type === 'all') {
    cache.clear();
  } else {
    // Clear specific cache keys
    for (const key of cache.keys()) {
      if (key.includes(type)) {
        cache.delete(key);
      }
    }
  }
};

// Invalidate cache after category operations
export const invalidateCategoryCache = () => {
  clearCache('categories');
  clearCache('categoryItems');
  clearCache('categoriesWithItems');
  clearCache('items'); // Items might be affected by category changes
};

// Enhanced category operation with immediate cache invalidation
export const performCategoryOperation = async (operation, ...args) => {
  try {
    const result = await operation(...args);
    
    // Invalidate cache immediately after successful operation
    invalidateCategoryCache();
    
    return result;
  } catch (error) {
    console.error('❌ Category operation failed:', error);
    throw error;
  }
};

// Delete an item with force flag
export const deleteItemAPI = async (itemId, force = false) => {
  try {
    const params = force ? '?force=true' : '';
    const response = await API.delete(`/items/${itemId}${params}`);
    
    // Clear relevant cache
    clearCache('items');
    clearCache('categoryItems');
    clearCache('categoriesWithItems');
    
    return response.data;
  } catch (error) {
    console.error('Error deleting item:', error);
    throw error;
  }
};

// Update category sequence numbers
export const updateCategorySequence = async (reorderedData) => {
  const response = await API.put(`/categories/sequence-numbers`, { categories: reorderedData });
  // Clear relevant cache
  clearCache('categories');
  return response.data;
};

export const updateItemSequence = async (categoryId, itemsToUpdate) => {
  const updatePromises = itemsToUpdate.map((itemData) => 
    API.put(`/items/${itemData.id}`, { sequence_number: itemData.sequence_number })
  );
  
  await Promise.all(updatePromises);
  clearCache('categoryItems');
  clearCache('items');
  return { success: true };
};

// Fetch item customizations
export const fetchItemCustomizationsAPI = async (itemId) => {
  const cacheKey = getCacheKey('customizations', { itemId });
  const cached = getCachedData(cacheKey);
  
  if (cached) {
    return cached;
  }

  const response = await API.get(`/items/${itemId}/customizations`);
  const data = response.data.data || [];
  
  setCachedData(cacheKey, data);
  return data;
};

// Fetch item categories
export const fetchItemCategoriesAPI = async (itemId) => {
  const cacheKey = getCacheKey('itemCategories', { itemId });
  const cached = getCachedData(cacheKey);
  
  if (cached) {
    return cached;
  }

  const response = await API.get(`/items/${itemId}/categories`);
  const data = response.data.data || [];
  
  setCachedData(cacheKey, data);
  return data;
};

// Fetch all customizations - FIXED VERSION
export const fetchAllCustomizationsAPI = async () => {
  try {
    
    // Check cache first
    const cacheKey = getCacheKey('allCustomizations', {});
    const cached = getCachedData(cacheKey);
    
    if (cached && cached.length > 0) {
      return cached;
    }
    
    
    // Use the paginated endpoint that returns ALL customizations (including those without related_items)
    const response = await API.get("/customizations?page=1&limit=1000");
    
    let data = [];
    if (response.data) {
      if (response.data.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      }
    }
    
    // Cache the data for future use
    setCachedData(cacheKey, data);
    
    return data;
    
  } catch (error) {
    console.error('❌ Error fetching customizations:', error);
    return [];
  }
};

// Fetch all categories
export const fetchAllCategoriesAPI = async () => {
  const cacheKey = getCacheKey('allCategories', {});
  const cached = getCachedData(cacheKey);
  
  if (cached) {
    return cached;
  }

  const response = await API.get("/categories");
  const data = response.data.data || [];
  
  setCachedData(cacheKey, data);
  return data;
}; 